import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { Folder, FolderItem } from '../types/folder.types';

import { fetchFolderDetail } from './fetchers/folder';
import { folderKeys } from './folder.keys';

/**
 * 폴더 상세의 퀴즈 목록을 끝까지 이어 붙인다.
 *
 * 첫 페이지는 이미 `folder.items` 에 있으므로 그 다음 커서부터 이어 받는다.
 * 순서는 서버가 준 순서 그대로 둔다 — 그 순서가 곧 상세·재생 순서다.
 */
export const useFolderItems = (folder: Folder) => {
  const { id, items: firstPage, nextCursor } = folder;

  const query = useInfiniteQuery({
    queryKey: [...folderKeys.detail(id), 'items', nextCursor] as const,
    queryFn: ({ pageParam }) => fetchFolderDetail(id, pageParam),
    initialPageParam: nextCursor as string | undefined,
    getNextPageParam: (lastPage: Folder) => lastPage.nextCursor,
    enabled: !!nextCursor,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const items = useMemo<FolderItem[]>(
    () => [
      ...firstPage,
      ...(query.data?.pages.flatMap(page => page.items) ?? []),
    ],
    [firstPage, query.data],
  );

  return {
    items,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
};
